import React from "react";
import { isEqual, flatMap, sortBy, get } from "lodash";
import classNames from "classnames";

import CSSModules from "lib/utils/css-modules";
import { Table, Loader } from "lib/components/vendor";
import { getDateRange } from "lib/utils/date";
import { dateFormat, availabilityTypes } from "../common";
import styles from "./Calendar.less";
import { groupSlotsByProductVariantId } from "./index";

@CSSModules(styles)
export default class Calendar extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      slotsByProductVariantId: groupSlotsByProductVariantId(props.slots),
    };
  }

  componentDidMount() {
    this.selection = new SelectionArea({
      selectables: [".calendar-cell"],
      boundaries: [".calendar-body"],
    })
      .on("start", ({ store: { stored }, event }) => {
        if (!event.ctrlKey && !event.metaKey) {
          stored.forEach((el) => {
            el.classList.remove("calendar-cell-selected");
          });
          this.selection.clearSelection();
        }
      })
      .on(
        "move",
        ({
          store: {
            changed: { added, removed },
          },
        }) => {
          added.forEach((el) => {
            el.classList.add("calendar-cell-selected");
          });
          removed.forEach((el) => {
            el.classList.remove("calendar-cell-selected");
          });
        }
      )
      .on("stop", ({ store: { selected, stored } }) => {
        this.selection.keepSelection();
        const selectedAvailabilities = [];
        const selectedPrices = [];
        stored.concat(selected).forEach((el) => {
          const classList = [...el.classList];
          const availabilityKey = classList.find((className) => className.startsWith("availability_cell"));
          if (availabilityKey) {
            const availabilityKeyParts = availabilityKey.split("_");
            const productVariantId = +availabilityKeyParts[3];
            const date = availabilityKeyParts[5];
            selectedAvailabilities.push({ productVariantId, date });
            return;
          }
          const priceKey = classList.find((className) => className.startsWith("price_cell"));
          if (priceKey) {
            const priceKeyParts = priceKey.split("_");
            const productVariantId = +priceKeyParts[3];
            const pricingPlanId = +priceKeyParts[5];
            const date = priceKeyParts[7];
            selectedPrices.push({ productVariantId, pricingPlanId, date });
          }
        });
        this.props.onSelectedChange(selectedAvailabilities, selectedPrices);
      });
  }

  componentDidUpdate(prevProps) {
    const { slots } = this.props;
    if (!isEqual(prevProps.slots, slots)) {
      this.setState({
        slotsByProductVariantId: groupSlotsByProductVariantId(slots),
      });
    }
  }

  render() {
    const { startDate, endDate, products, pricingPlans, loading, currentCompany, isFormOpen } = this.props;
    const { slotsByProductVariantId } = this.state;

    const now = moment();
    const todayStr = now.format(dateFormat);
    const todayObj = moment(todayStr);
    const dateRange = getDateRange(startDate, endDate);
    const productVariants = sortBy(
      flatMap(
        products.map((product) =>
          (product.product_variants || []).map((pv) => ({
            id: pv.id,
            name: pv.name || product.name,
            url: product.getPreviewUrlFor(currentCompany),
          }))
        )
      ),
      (p) => p.name.toLowerCase()
    );

    return (
      <div styleName={classNames("accommodation-calendar", { "form-open": isFormOpen })}>
        <Table celled className="calendar-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              {dateRange.map((date) => {
                const dateObj = moment(date);
                const today = date === todayStr;
                const weekend = [6, 0].includes(dateObj.day());
                const past = dateObj < todayObj;
                return (
                  <Table.HeaderCell
                    key={`header_${date}`}
                    textAlign="center"
                    title={dateObj.format(`dddd, ${dateFormat}`)}
                    className={classNames("date-cell", { today, weekend, past })}
                  >
                    {dateObj.format("DD MMM")}
                  </Table.HeaderCell>
                );
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body className="calendar-body">
            {productVariants.map((pv, idx) => [
              <Table.Row>
                <Table.Cell className="product-variant product-variant-name">
                  {pv.name}{" "}
                  {pv.url && (
                    <a href={pv.url} target="_blank" className="offer-link">
                      <i className="icon material-icons">open_in_new</i>
                    </a>
                  )}
                </Table.Cell>
                {loading && !idx && (
                  <Table.Cell colSpan={dateRange.length} rowSpan={(1 + pricingPlans.length) * productVariants.length}>
                    <Loader active inline="centered" />
                  </Table.Cell>
                )}
                {!loading &&
                  dateRange.map((date) => {
                    const key = `cell_pv_${pv.id}_date_${date}`;
                    const past = moment(date) < todayObj;
                    const slot = get(slotsByProductVariantId, `${pv.id}.${date}`);
                    const isInstantAvailability = get(slot, "availability_type") === availabilityTypes.instant;
                    const minNightStay = get(slot, "min_night_stay");
                    return (
                      <Table.Cell
                        key={key}
                        textAlign="center"
                        disabled={past}
                        className={classNames(
                          "calendar-cell",
                          "product-variant",
                          `availability_${key}`,
                          { today: date === todayStr },
                          { past }
                        )}
                      >
                        {get(slot, "total_stock_left")}
                        {isInstantAvailability && (
                          <img
                            src="/images/flash.svg"
                            styleName="instant-availability"
                            title={I18n.t(
                              `papp.accommodation.AccommodationSchedulerView.availabilityTypes.${availabilityTypes.instant}`
                            )}
                          />
                        )}
                        {minNightStay > 1 && (
                          <span
                            styleName="min-night-stay"
                            title={`${I18n.t("attributes.min_nights_count")}: ${minNightStay}`}
                          >
                            {minNightStay}
                          </span>
                        )}
                      </Table.Cell>
                    );
                  })}
              </Table.Row>,
              pricingPlans.map((pricingPlan) => (
                <Table.Row>
                  <Table.Cell className="pricing-plan-name">{pricingPlan.name}</Table.Cell>
                  {!loading &&
                    dateRange.map((date) => {
                      const key = `cell_pv_${pv.id}_pp_${pricingPlan.id}_date_${date}`;
                      return (
                        <Table.Cell
                          key={key}
                          textAlign="center"
                          disabled={moment(date) < todayObj}
                          className={classNames("calendar-cell", `price_${key}`, { today: date === todayStr })}
                        >
                          {get(
                            slotsByProductVariantId,
                            `${pv.id}.${date}.pricings_by_pricing_plan_id.${pricingPlan.id}.price.value`
                          )}
                        </Table.Cell>
                      );
                    })}
                </Table.Row>
              )),
            ])}
          </Table.Body>
        </Table>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/accommodation/AccommodationCalendarView/Calendar.jsx